/**
 * Data Integrity Utilities
 * 
 * Client-side integrity checks and diagnostics for Wealthica data
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

export interface DataIntegrityCheck {
  check_name: string;
  status: 'PASS' | 'WARNING' | 'FAIL';
  issues_count: number;
  details: Record<string, any>;
}

export interface PersistenceMetrics {
  attempts: number;
  successes: number;
  failures: number;
  lastAttempt: string | null;
  lastSuccess: string | null;
  errorRate: number;
}

/**
 * Run data integrity checks for a user
 */
export async function runDataIntegrityChecks(
  userId?: string
): Promise<{ checks: DataIntegrityCheck[]; success: boolean; error?: string }> {
  try {
    logger.info('data-integrity', 'Running integrity checks', { userId });

    const { data, error } = await supabase.rpc('check_data_integrity', {
      p_user_id: userId || null
    });

    if (error) {
      logger.error('data-integrity', 'Failed to run integrity checks', { error });
      return { checks: [], success: false, error: error.message };
    }

    logger.info('data-integrity', 'Integrity checks complete', { 
      checksRun: data?.length || 0,
      issues: data?.filter((c: DataIntegrityCheck) => c.status !== 'PASS').length || 0
    });


    // Type assertion to ensure proper typing
    const typedChecks: DataIntegrityCheck[] = (data || []).map((check: any) => ({
      ...check,
      status: check.status as 'PASS' | 'WARNING' | 'FAIL'
    }));
    
    return { checks: typedChecks, success: true };
  } catch (error) {
    logger.error('data-integrity', 'Unexpected error running integrity checks', { error });
    return { checks: [], success: false, error: String(error) };
  }
}

/**
 * Track persistence operation metrics
 */
export class PersistenceMetricsTracker {
  private static readonly STORAGE_KEY = 'altsiq:persistence-metrics';
  private static readonly MULTI_STORE_KEY = 'altsiq:multi-store-metrics';
  
  static getMetrics(): PersistenceMetrics {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      logger.warn('data-integrity', 'Failed to parse stored metrics', { error });
    }
    
    return {
      attempts: 0,
      successes: 0,
      failures: 0,
      lastAttempt: null,
      lastSuccess: null,
      errorRate: 0
    };
  }

  /**
   * MULTI-STORE: Get enhanced metrics with per-institution tracking
   */
  static getMultiStoreMetrics(): PersistenceMetrics & { institutionMetrics: Record<string, any> } {
    try {
      const stored = localStorage.getItem(this.MULTI_STORE_KEY);
      const baseMetrics = this.getMetrics();
      
      if (stored) {
        const multiData = JSON.parse(stored);
        return {
          ...baseMetrics,
          institutionMetrics: multiData.institutionMetrics || {}
        };
      }
    } catch (error) {
      logger.warn('data-integrity', 'Failed to parse multi-store metrics', { error });
    }
    
    return {
      ...this.getMetrics(),
      institutionMetrics: {}
    };
  }

  /**
   * MULTI-STORE: Record metrics for specific institution
   */
  static recordInstitutionAttempt(institutionId: string): void {
    this.recordAttempt();
    this.updateInstitutionMetrics(institutionId, 'attempt');
  }

  static recordInstitutionSuccess(institutionId: string): void {
    this.recordSuccess();
    this.updateInstitutionMetrics(institutionId, 'success');
  }

  static recordInstitutionFailure(institutionId: string): void {
    this.recordFailure();
    this.updateInstitutionMetrics(institutionId, 'failure');
  }

  private static updateInstitutionMetrics(institutionId: string, type: 'attempt' | 'success' | 'failure'): void {
    try {
      const multiMetrics = this.getMultiStoreMetrics();
      const institutionMetrics = multiMetrics.institutionMetrics[institutionId] || {
        attempts: 0,
        successes: 0,
        failures: 0,
        lastOperation: null
      };

      if (type === 'attempt') {
        institutionMetrics.attempts++;
      } else if (type === 'success') {
        institutionMetrics.successes++;
      } else if (type === 'failure') {
        institutionMetrics.failures++;
      }

      institutionMetrics.lastOperation = new Date().toISOString();
      multiMetrics.institutionMetrics[institutionId] = institutionMetrics;

      localStorage.setItem(this.MULTI_STORE_KEY, JSON.stringify({
        institutionMetrics: multiMetrics.institutionMetrics
      }));
    } catch (error) {
      logger.warn('data-integrity', 'Failed to update institution metrics', { error, institutionId });
    }
  }
  
  static recordAttempt(): void {
    const metrics = this.getMetrics();
    metrics.attempts++;
    metrics.lastAttempt = new Date().toISOString();
    this.saveMetrics(metrics);
  }
  
  static recordSuccess(): void {
    const metrics = this.getMetrics();
    metrics.successes++;
    metrics.lastSuccess = new Date().toISOString();
    metrics.errorRate = metrics.attempts > 0 ? metrics.failures / metrics.attempts : 0;
    this.saveMetrics(metrics);
  }
  
  static recordFailure(): void {
    const metrics = this.getMetrics();
    metrics.failures++;
    metrics.errorRate = metrics.attempts > 0 ? metrics.failures / metrics.attempts : 0;
    this.saveMetrics(metrics);
  }
  
  static reset(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.MULTI_STORE_KEY);
  }

  /**
   * MULTI-STORE: Process integrity data from Edge Function responses
   */
  static processEdgeFunctionIntegrity(integrityData: any, institutionId: string): void {
    if (!integrityData) return;

    const { summary, auto_cleanup_performed, persistence_metrics } = integrityData;

    // Log integrity summary
    if (summary) {
      logger.info('data-integrity', 'Edge Function integrity summary', {
        institutionId,
        totalChecks: summary.total_checks,
        failures: summary.failures,
        warnings: summary.warnings,
        autoCleanup: auto_cleanup_performed
      });

      // Update metrics based on integrity results
      if (summary.failures > 0) {
        this.recordInstitutionFailure(institutionId);
      } else {
        this.recordInstitutionSuccess(institutionId);
      }
    }

    // Process persistence performance metrics
    if (persistence_metrics) {
      logger.debug('data-integrity', 'Edge Function performance', {
        institutionId,
        operationType: persistence_metrics.operation_type,
        duration: persistence_metrics.duration_ms,
        recordsProcessed: persistence_metrics.records_processed
      });
    }
  }
  
  private static saveMetrics(metrics: PersistenceMetrics): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(metrics));
    } catch (error) {
      logger.warn('data-integrity', 'Failed to save metrics', { error });
    }
  }
}

/**
 * Validate account consistency between cache and database
 */
export async function validateAccountConsistency(
  userId: string,
  cachedAccounts: any[],
  institutionId?: string
): Promise<{
  consistent: boolean;
  databaseCount: number;
  cacheCount: number;
  discrepancies: string[];
}> {
  try {
    let query = supabase
      .from('investment_accounts')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);
    
    if (institutionId) {
      // Look up institution UUID first
      const instQuery = supabase
        .from('investment_institutions')
        .select('id')
        .eq('institution_identifier', institutionId)
        .eq('provider_type', 'wealthica') as any;

      const { data: institution } = await instQuery.maybeSingle();
      
      if (institution?.id) {
        query = query.eq('institution_id', institution.id);
      }
    }
    
    const { count, error } = await query;
    
    if (error) {
      logger.error('data-integrity', 'Failed to validate account consistency', { error });
      return {
        consistent: false,
        databaseCount: 0,
        cacheCount: cachedAccounts.length,
        discrepancies: [`Database query failed: ${error.message}`]
      };
    }
    
    const databaseCount = count || 0;
    const cacheCount = cachedAccounts.length;
    const discrepancies: string[] = [];
    
    if (databaseCount !== cacheCount) {
      discrepancies.push(
        `Count mismatch: Database has ${databaseCount}, cache has ${cacheCount}`
      );
    }
    
    logger.info('data-integrity', 'Account consistency check', {
      databaseCount,
      cacheCount,
      consistent: discrepancies.length === 0,
      institutionId
    });
    
    return {
      consistent: discrepancies.length === 0,
      databaseCount,
      cacheCount,
      discrepancies
    };
    
  } catch (error) {
    logger.error('data-integrity', 'Error validating account consistency', { error });
    return {
      consistent: false,
      databaseCount: 0,
      cacheCount: cachedAccounts.length,
      discrepancies: [`Validation error: ${String(error)}`]
    };
  }
}

/**
 * Idempotent account upsert using database function
 */
export async function upsertAccount(accountData: {
  userId: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  currentBalance: number;
  currency: string;
  institutionId?: string;
  description?: string;
  registered?: string;
  cdnAssetClass?: string;
  rawData?: any;
}): Promise<{ success: boolean; accountId?: string; error?: string }> {
  try {
    const { data, error } = await supabase.rpc('upsert_investment_account', {
      p_user_id: accountData.userId,
      p_account_number: accountData.accountNumber,
      p_account_name: accountData.accountName,
      p_account_type: accountData.accountType,
      p_current_balance: accountData.currentBalance,
      p_currency: accountData.currency,
      p_institution_id: accountData.institutionId || null,
      p_description: accountData.description || null,
      p_registered: accountData.registered || null,
      p_cdn_asset_class: accountData.cdnAssetClass || null,
      p_raw_data: accountData.rawData || null
    });

    if (error) {
      logger.error('data-integrity', 'Failed to upsert account', { error });
      return { success: false, error: error.message };
    }

    return { success: true, accountId: data };
  } catch (error) {
    logger.error('data-integrity', 'Error upserting account', { error });
    return { success: false, error: String(error) };
  }
}

/**
 * Idempotent holding upsert using database function
 */
export async function upsertHolding(holdingData: {
  userId: string;
  accountUuid: string;
  accountId: string;
  assetName: string;
  assetType: string;
  symbol?: string;
  quantity?: number;
  marketValue: number;
  currency: string;
  bookValue?: number;
  price?: number;
  averageCost?: number;
  securityType?: string;
  description?: string;
  registered?: string;
  providerName?: string;
  rawData?: any;
}): Promise<{ success: boolean; holdingId?: string; error?: string }> {
  try {
    const { data, error } = await supabase.rpc('upsert_investment_holding', {
      p_user_id: holdingData.userId,
      p_account_uuid: holdingData.accountUuid,
      p_account_id: holdingData.accountId,
      p_asset_name: holdingData.assetName,
      p_asset_type: holdingData.assetType,
      p_symbol: holdingData.symbol || null,
      p_quantity: holdingData.quantity || null,
      p_market_value: holdingData.marketValue,
      p_currency: holdingData.currency,
      p_book_value: holdingData.bookValue || null,
      p_price: holdingData.price || null,
      p_average_cost: holdingData.averageCost || null,
      p_security_type: holdingData.securityType || null,
      p_description: holdingData.description || null,
      p_registered: holdingData.registered || 'non-registered',
      p_provider_name: holdingData.providerName || 'wealthica',
      p_raw_data: holdingData.rawData || null
    });

    if (error) {
      logger.error('data-integrity', 'Failed to upsert holding', { error });
      return { success: false, error: error.message };
    }

    return { success: true, holdingId: data };
  } catch (error) {
    logger.error('data-integrity', 'Error upserting holding', { error });
    return { success: false, error: String(error) };
  }
}

/**
 * Clean up orphaned data
 */
export async function cleanupOrphanedData(
  userId: string
): Promise<{ success: boolean; cleaned: { accounts: number; holdings: number }; error?: string }> {
  try {
    // Clean up holdings without valid accounts
    const { count: orphanedHoldings, error: holdingsError } = await supabase
      .from('investment_holdings')
      .delete()
      .eq('user_id', userId)
      .not('account_uuid', 'in', `(
        SELECT id FROM investment_accounts WHERE user_id = '${userId}'
      )`);

    if (holdingsError) {
      logger.error('data-integrity', 'Failed to clean orphaned holdings', { holdingsError });
      return { success: false, cleaned: { accounts: 0, holdings: 0 }, error: holdingsError.message };
    }

    // Clean up accounts without valid institutions (optional - might be legitimate)
    const { count: orphanedAccounts } = await supabase
      .from('investment_accounts')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .is('institution_id', null);

    logger.info('data-integrity', 'Cleaned up orphaned data', {
      userId,
      orphanedHoldings: orphanedHoldings || 0,
      orphanedAccounts: orphanedAccounts || 0
    });

    return {
      success: true,
      cleaned: {
        accounts: 0, // We don't auto-delete accounts without institutions
        holdings: orphanedHoldings || 0
      }
    };
  } catch (error) {
    logger.error('data-integrity', 'Error cleaning orphaned data', { error });
    return { success: false, cleaned: { accounts: 0, holdings: 0 }, error: String(error) };
  }
}
